import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { 
  ArrowLeft, 
  Bell,
  Plus,
  Trash2,
  AlertTriangle,
  CheckCircle,
  Loader2,
  DollarSign
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = ["Food", "Transport", "Shopping", "Bills", "Entertainment", "Health", "Other"];

const BudgetLimitsPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [limits, setLimits] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [totalLimit, setTotalLimit] = useState({ enabled: false, amount: "" });

  useEffect(() => {
    const storedUser = localStorage.getItem("tamba_user");
    if (!storedUser) {
      navigate("/auth");
      return;
    }
    const userData = JSON.parse(storedUser);
    setUser(userData);
    fetchData(userData.id);
  }, [navigate]);

  const fetchData = async (userId) => {
    try {
      const [limitsRes, alertsRes] = await Promise.all([
        axios.get(`${API}/budget/limits/${userId}`),
        axios.get(`${API}/budget/alerts/${userId}`)
      ]);
      
      const existingLimits = limitsRes.data.limits || [];
      
      // Separate total limit from category limits
      const totalLimitData = existingLimits.find(l => !l.category);
      const categoryLimits = existingLimits.filter(l => l.category);
      
      if (totalLimitData) {
        setTotalLimit({ enabled: true, amount: totalLimitData.amount.toString() });
      }
      
      setLimits(categoryLimits.map(l => ({
        category: l.category,
        amount: l.amount.toString(),
        enabled: true
      })));
      
      setAlerts(alertsRes.data.alerts || []);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCategory = () => {
    const usedCategories = limits.map(l => l.category);
    const availableCategories = CATEGORIES.filter(c => !usedCategories.includes(c));
    
    if (availableCategories.length === 0) {
      toast.info("All categories have limits set");
      return;
    }
    
    setLimits([...limits, { 
      category: availableCategories[0], 
      amount: "", 
      enabled: true 
    }]);
  };

  const handleRemoveCategory = (index) => {
    setLimits(limits.filter((_, i) => i !== index));
  };

  const handleUpdateLimit = (index, field, value) => {
    const updated = [...limits];
    updated[index][field] = value;
    setLimits(updated);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const allLimits = [];
      
      // Add total limit if enabled
      if (totalLimit.enabled && totalLimit.amount) {
        allLimits.push({
          category: null,
          amount: parseFloat(totalLimit.amount),
          period: "monthly"
        });
      }
      
      // Add category limits
      limits.forEach(limit => {
        if (limit.enabled && limit.amount) {
          allLimits.push({
            category: limit.category,
            amount: parseFloat(limit.amount),
            period: "monthly"
          });
        }
      });
      
      await axios.post(`${API}/budget/limits/${user.id}`, {
        user_id: user.id,
        limits: allLimits
      });
      
      toast.success("Budget limits saved!");
      fetchData(user.id);
    } catch (error) {
      toast.error("Failed to save limits");
    } finally {
      setSaving(false);
    }
  };

  const currencySymbol = user?.currency_symbol || "$";

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-tamba-coral" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border/50 px-4 py-3">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate("/settings")}
              data-testid="back-btn"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="font-heading text-xl font-semibold">Budget Alerts</h1>
              <p className="text-xs text-muted-foreground">Set spending limits</p>
            </div>
          </div>
          <Button 
            onClick={handleSave}
            disabled={saving}
            className="rounded-full bg-tamba-coral hover:bg-tamba-coral/90"
            data-testid="save-limits-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
          </Button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* Active Alerts */}
        {alerts.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h2 className="font-heading text-lg font-semibold mb-3 flex items-center gap-2">
              <Bell className="h-5 w-5 text-tamba-coral" />
              Active Alerts
            </h2>
            <div className="space-y-3">
              {alerts.map((alert, index) => (
                <Card 
                  key={index} 
                  className={`border-2 ${
                    alert.type === "exceeded" 
                      ? "border-red-500/50 bg-red-50/50" 
                      : "border-tamba-yellow/50 bg-tamba-yellow/5"
                  }`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className={`h-5 w-5 flex-shrink-0 mt-0.5 ${
                        alert.type === "exceeded" ? "text-red-500" : "text-tamba-yellow"
                      }`} />
                      <div className="flex-1">
                        <p className="font-medium">{alert.category} Budget</p>
                        <p className="text-sm text-muted-foreground">{alert.message}</p>
                        <div className="mt-2 h-2 bg-secondary rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full ${
                              alert.type === "exceeded" ? "bg-red-500" : "bg-tamba-yellow"
                            }`}
                            style={{ width: `${Math.min(alert.percentage, 100)}%` }}
                          />
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {currencySymbol}{alert.spent.toFixed(2)} / {currencySymbol}{alert.limit.toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </motion.div>
        )}

        {/* Total Monthly Limit */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="border-tamba-coral/30 bg-gradient-to-br from-tamba-coral/5 to-transparent">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-tamba-coral" />
                  Total Monthly Budget
                </CardTitle>
                <Switch
                  checked={totalLimit.enabled}
                  onCheckedChange={(checked) => setTotalLimit(prev => ({ ...prev, enabled: checked }))}
                />
              </div>
            </CardHeader>
            {totalLimit.enabled && (
              <CardContent className="pt-2">
                <div className="flex items-center gap-3">
                  <span className="text-2xl font-bold">{currencySymbol}</span>
                  <Input
                    type="number"
                    placeholder="5000"
                    value={totalLimit.amount}
                    onChange={(e) => setTotalLimit(prev => ({ ...prev, amount: e.target.value }))}
                    className="text-2xl font-bold h-14"
                    data-testid="total-limit-input"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Get alerted when total spending exceeds this amount
                </p>
              </CardContent>
            )}
          </Card>
        </motion.div>

        {/* Category Limits */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading text-lg font-semibold">Category Limits</h2>
            <Button
              variant="outline"
              size="sm"
              onClick={handleAddCategory}
              className="gap-1"
              data-testid="add-category-btn"
            >
              <Plus className="h-4 w-4" />
              Add
            </Button>
          </div>

          {limits.length === 0 ? (
            <Card className="border-dashed border-2 border-border/50">
              <CardContent className="py-8 text-center">
                <Bell className="h-10 w-10 mx-auto mb-3 text-muted-foreground/50" />
                <p className="text-muted-foreground">No category limits set</p>
                <p className="text-sm text-muted-foreground">Add limits to get alerts for specific categories</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {limits.map((limit, index) => (
                <Card key={index} className="border-border/50">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <div className="flex-1">
                        <Label className="text-sm text-muted-foreground">Category</Label>
                        <select
                          value={limit.category}
                          onChange={(e) => handleUpdateLimit(index, "category", e.target.value)}
                          className="w-full mt-1 p-2 rounded-lg border border-border bg-background"
                        >
                          {CATEGORIES.map(cat => (
                            <option key={cat} value={cat}>{cat}</option>
                          ))}
                        </select>
                      </div>
                      <div className="flex-1">
                        <Label className="text-sm text-muted-foreground">Limit ({currencySymbol})</Label>
                        <Input
                          type="number"
                          placeholder="500"
                          value={limit.amount}
                          onChange={(e) => handleUpdateLimit(index, "amount", e.target.value)}
                          className="mt-1"
                        />
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemoveCategory(index)}
                        className="text-muted-foreground hover:text-red-500 mt-5"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </motion.div>

        {/* Info */}
        <Card className="border-tamba-teal/30 bg-gradient-to-br from-tamba-teal/5 to-transparent">
          <CardContent className="p-4">
            <h3 className="font-medium mb-2 flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-tamba-teal" />
              How Alerts Work
            </h3>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• You'll see a warning at 80% of your limit</li>
              <li>• You'll see an alert when you exceed your limit</li>
              <li>• Alerts are shown on your Budget page</li>
              <li>• Limits reset at the start of each month</li>
            </ul>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default BudgetLimitsPage;
